import {
  Button,
  Icon,
  IconButton,
  Tag,
  TextCaption,
  TextTitle,
} from '@pypestream/design-system';
import { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { tableStore } from '../stores/table-store';
import { Channel } from '../utils';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import {
  channelSettingsPath,
  copyEmbedCode,
  generateEmbedScript,
} from './channel-actions';
import { ChannelBoundConnections } from './design-channel-bound';
import { resolvePypeNextAccess } from './pype-designer/resolve-pype-next-access';

/**
 * One channel's settings, in place in the Design channel panel — the
 * prototype's channel detail panel. For a Pype: what identifies it (its
 * details, its embed code, what it's connected to in Project settings), with
 * how it looks one click away in Advanced settings. For Voice: per-channel
 * wording (not stored anywhere yet, so shown disabled) and its telephony
 * settings, read-only here.
 */

const ReadOnlyField: FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => (
  <div className="c-design-channel-detail__field">
    <span className="c-design-channel-detail__label">{label}</span>
    <input
      className="c-design-channel-detail__input"
      readOnly
      value={value?.trim() || '—'}
    />
  </div>
);

const VoiceTelephony: FC<{ channel: Channel }> = ({ channel }) => {
  const cfg = channel.twilioVoiceConfig;
  const voiceName =
    (cfg?.elevenlabs_voice_metadata as { name?: string } | undefined)?.name ??
    cfg?.voice;
  return (
    <div data-testid="bobcat-design-channel-voice">
      <TextCaption variant="tertiary">
        Voice never boots the Pype widget — no colors, radius, or CSS apply
        here. These are the settings this voice channel runs on.
      </TextCaption>
      <ReadOnlyField label="Twilio phone number" value={cfg?.phone_number} />
      <ReadOnlyField
        label={`Text-to-speech${cfg?.tts_provider ? ` (${cfg.tts_provider})` : ''}`}
        value={voiceName}
      />
      <ReadOnlyField
        label="Speech-to-text provider"
        value={cfg?.transcription_provider}
      />
      <ReadOnlyField
        label="Retry prompt (on unclear input)"
        value={cfg?.retry_prompt_prefix}
      />
    </div>
  );
};

/** The Advanced settings link: where everything the panel doesn't hold lives. */
const AdvancedSettingsLink: FC<{
  inDesigner: boolean;
  isVoice: boolean;
  onOpen: () => void;
}> = ({ inDesigner, isVoice, onOpen }) => (
  <button
    type="button"
    className="c-design-channel-detail__advanced"
    data-testid="bobcat-design-channel-all-settings"
    onClick={onOpen}
  >
    <span className="c-design-channel-detail__advanced-icon">
      <Icon name="elements-sliders" size="small" />
    </span>
    <span className="c-design-channel-detail__advanced-body">
      <span className="c-design-channel-detail__advanced-title">
        {inDesigner ? 'Advanced settings' : 'All channel settings'}
      </span>
      <span className="c-design-channel-detail__advanced-sub">
        {inDesigner
          ? 'Shell, branding, theme and custom CSS, with a live preview.'
          : isVoice
            ? 'Phone number, voice, transcription and every other setting this channel runs on.'
            : 'Every setting this channel runs on.'}
      </span>
    </span>
    <Icon name="chevron-right" size="xsmall" />
  </button>
);

const Section: FC<{
  title?: string;
  action?: JSX.Element;
  children: ReactNode;
}> = ({ title, action, children }) => (
  <section className="c-design-channel-detail__sec">
    {title && (
      <div className="c-design-channel-detail__sec-head">
        <h3 className="c-design-channel-detail__sec-title">{title}</h3>
        {action}
      </div>
    )}
    {children}
  </section>
);

export const DesignChannelDetail: FC<{
  channel: Channel;
  /** Back to this type's channel list. */
  onBack: () => void;
  /** Close the panel altogether, as the prototype's ×. */
  onClose: () => void;
}> = ({ channel, onBack, onClose }) => {
  const navigate = useNavigate();
  const { botId, projectId } = useProStudioCtxSelector((ctx) => ({
    projectId: ctx.project?.id,
    botId:
      ctx.selectedBot?.uniqueId ||
      (ctx.solutions || []).find(
        ({ botSolutionId }) => ctx.selectedBot?.id === botSolutionId
      )?.id,
  }));
  const isVoice = channel.type === 'twilio_voice';
  const pypeNext = resolvePypeNextAccess(tableStore.featureFlags);
  const inDesigner = !isVoice && pypeNext === 'allowed';
  const openAdvanced = () =>
    navigate(
      inDesigner && projectId && botId
        ? `/${projectId}/${botId}/channels/${channel.id}/pype-designer`
        : channelSettingsPath(projectId, botId, channel)
    );

  const head = (sub: string) => (
    <div className="c-design-channel-detail__head">
      <IconButton
        name="chevron-left"
        size="small"
        aria-label="Back to channels"
        test-id="bobcat-design-channel-detail-back"
        onClick={onBack}
      />
      <div className="c-design-channel-detail__title">
        <TextTitle size="xsmall">{channel.name}</TextTitle>
        <TextCaption variant="secondary">{sub}</TextCaption>
      </div>
      <IconButton
        name="close"
        size="small"
        aria-label="Close"
        test-id="bobcat-design-channel-detail-close"
        onClick={onClose}
      />
    </div>
  );

  const foot = (
    <div className="c-design-channel-detail__foot">
      <Button
        size="small"
        test-id="bobcat-design-channel-detail-done"
        onClick={onBack}
      >
        Done
      </Button>
    </div>
  );

  // A Pype channel's panel is only what identifies it (prototype's Pype
  // channel panel, Sep 29): its details, the code to embed it, and what it's
  // connected to. How it looks lives in Advanced settings, one click away.
  // The header and footer stay put; only the sections between them scroll.
  if (!isVoice) {
    const embed = generateEmbedScript(channel.id, channel.env).trim();
    return (
      <div
        className="c-design-channel-detail c-design-channel-detail--pinned"
        data-testid="bobcat-design-channel-detail"
      >
        {head(
          'Details, embed code and connections. Look and feel is in Advanced settings.'
        )}
        <div className="c-design-channel-detail__body">
          <Section title="Details">
            <ReadOnlyField label="Channel name" value={channel.name} />
            <div className="c-design-channel-detail__field">
              <span className="c-design-channel-detail__label">
                Environment
              </span>
              <input
                className="c-design-channel-detail__input"
                readOnly
                value={channel.env === 'live' ? 'Live' : 'Sandbox'}
              />
              <TextCaption variant="tertiary">
                Cannot be changed after the channel is created.
              </TextCaption>
            </div>
          </Section>
          <Section
            title="Embed code"
            action={
              <Button
                size="small"
                variant="secondary"
                test-id="bobcat-design-channel-embed-copy"
                onClick={() =>
                  copyEmbedCode(
                    channel,
                    (_key, { defaultValue }) => defaultValue
                  )
                }
              >
                <Icon slot="prefix" name="copy" size="xsmall" />
                Copy
              </Button>
            }
          >
            <pre
              className="c-design-channel-detail__embed"
              data-testid="bobcat-design-channel-embed"
            >
              {embed}
            </pre>
            <TextCaption variant="tertiary">
              Paste it before the closing &lt;/body&gt; tag on every page that
              should show the chat.
            </TextCaption>
          </Section>
          <Section title="Connected in project settings">
            <ChannelBoundConnections channel={channel} />
          </Section>
          <Section>
            <AdvancedSettingsLink
              inDesigner={inDesigner}
              isVoice={false}
              onOpen={openAdvanced}
            />
          </Section>
        </div>
        {foot}
      </div>
    );
  }

  return (
    <div
      className="c-design-channel-detail"
      data-testid="bobcat-design-channel-detail"
    >
      {head(
        'Per-channel wording, plus whatever configuration this channel actually runs on.'
      )}
      <div className="c-design-channel-detail__field">
        <span className="c-design-channel-detail__label">
          Wording override (optional){' '}
          <Tag size="small" variant="default">
            Coming soon
          </Tag>
        </span>
        <textarea
          className="c-design-channel-detail__text"
          data-testid="bobcat-design-channel-wording"
          disabled
          placeholder="Leave blank to use the global instructions as-is on this channel."
        />
      </div>
      <p className="c-design-channel-detail__eyebrow">Telephony & Voice</p>
      <VoiceTelephony channel={channel} />
      <AdvancedSettingsLink inDesigner={false} isVoice onOpen={openAdvanced} />
      {foot}
    </div>
  );
};
